import { useState, useMemo } from "react";
import { useQuery, useAction, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  User,
  MoreVertical,
  KeyRound,
  Shield,
  Eye,
  EyeOff,
  Check,
  X,
  Search,
  Plus,
  AlertCircle,
  UserX,
  UserCheck,
} from "lucide-react";
import type { Id } from "../../convex/_generated/dataModel";
import { format } from "date-fns";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const USER_NAME_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});
const USER_ROLES = ["admin", "staff", "trainee"] as const;

type UserRole = (typeof USER_ROLES)[number];

type ManagedUser = {
  _id: Id<"users">;
  email: string;
  name: string;
  role: UserRole | null;
  is_active: boolean;
  deleted_at: number | null;
  deleted_by: Id<"users"> | null;
  created_at: number;
};

const isUserRole = (value: string): value is UserRole => {
  return USER_ROLES.includes(value as UserRole);
};

const getUserEmailLabel = (user: ManagedUser | null) => {
  if (user === null) {
    return "this user";
  }

  return user.email;
};

// Radix can leave this inline lock behind when a menu-triggered dialog closes.
const clearStaleDialogPointerLock = () => {
  if (typeof document === "undefined") {
    return;
  }

  if (document.body.style.pointerEvents !== "none") {
    return;
  }

  document.body.style.pointerEvents = "";
};

const scheduleDialogLayerCleanup = () => {
  globalThis.setTimeout(clearStaleDialogPointerLock, 0);
};

// Let the dropdown finish closing before opening a modal dialog from its item.
const openDialogAfterDropdownCloses = (openDialog: () => void) => {
  globalThis.setTimeout(openDialog, 0);
};

const ROLE_COLORS = {
  admin: "bg-purple-100 text-purple-800",
  staff: "bg-blue-100 text-blue-800",
  trainee: "bg-yellow-100 text-yellow-800",
} satisfies Record<UserRole, string>;

export function UserManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedUser, setSelectedUser] = useState<ManagedUser | null>(null);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showChangeRoleDialog, setShowChangeRoleDialog] = useState(false);

  // password reset form state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // role change form state
  const [newRole, setNewRole] = useState<"admin" | "staff" | "trainee">(
    "staff"
  );
  const [isChangingRole, setIsChangingRole] = useState(false);

  // create user form state
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [createUserEmail, setCreateUserEmail] = useState("");
  const [createUserName, setCreateUserName] = useState("");
  const [createUserPassword, setCreateUserPassword] = useState("");
  const [createUserConfirmPassword, setCreateUserConfirmPassword] =
    useState("");
  const [createUserRole, setCreateUserRole] = useState<
    "admin" | "staff" | "trainee"
  >("staff");
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showCreateConfirmPassword, setShowCreateConfirmPassword] =
    useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  // deactivate user state
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [userToDeactivate, setUserToDeactivate] =
    useState<ManagedUser | null>(null);
  const [isDeactivating, setIsDeactivating] = useState(false);

  // reactivate user state
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [userToReactivate, setUserToReactivate] =
    useState<ManagedUser | null>(null);
  const [isReactivating, setIsReactivating] = useState(false);

  // fetch users
  const users = useQuery(api.admin.listAllUsers);
  const currentUser = useQuery(api.users.viewer);
  const resetPassword = useAction(api.admin.resetUserPassword);
  const changeRole = useMutation(api.admin.setUserRole);
  const createUser = useAction(api.admin.createUser);
  const deactivateUser = useMutation(api.admin.deactivateUser);
  const reactivateUser = useMutation(api.admin.reactivateUser);

  // filter and paginate users
  const filteredUsers = useMemo(() => {
    if (!users) return [];

    const lowerSearch = searchTerm.toLowerCase();
    const matchingUsers = users.filter(
      (user) =>
        user.email.toLowerCase().includes(lowerSearch) ||
        user.name.toLowerCase().includes(lowerSearch)
    );

    return matchingUsers.sort((firstUser, secondUser) => {
      const nameOrder = USER_NAME_COLLATOR.compare(
        firstUser.name,
        secondUser.name
      );

      if (nameOrder !== 0) {
        return nameOrder;
      }

      return USER_NAME_COLLATOR.compare(firstUser.email, secondUser.email);
    });
  }, [users, searchTerm]);

  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // password validation
  const passwordValidation = useMemo(() => {
    return {
      hasMinLength: newPassword.length >= 6,
      hasUppercase: /[A-Z]/.test(newPassword),
      hasLowercase: /[a-z]/.test(newPassword),
      hasNumber: /\d/.test(newPassword),
      passwordsMatch: newPassword === confirmPassword && newPassword.length > 0,
    };
  }, [newPassword, confirmPassword]);

  const isPasswordValid =
    passwordValidation.hasMinLength &&
    passwordValidation.hasUppercase &&
    passwordValidation.hasLowercase &&
    passwordValidation.hasNumber &&
    passwordValidation.passwordsMatch;

  // create user password validation
  const createUserPasswordValidation = useMemo(() => {
    return {
      hasMinLength: createUserPassword.length >= 6,
      hasUppercase: /[A-Z]/.test(createUserPassword),
      hasLowercase: /[a-z]/.test(createUserPassword),
      hasNumber: /\d/.test(createUserPassword),
      passwordsMatch:
        createUserPassword === createUserConfirmPassword &&
        createUserPassword.length > 0,
    };
  }, [createUserPassword, createUserConfirmPassword]);

  const isCreateUserValid =
    createUserEmail.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createUserEmail) &&
    createUserName.trim().length > 0 &&
    createUserPasswordValidation.hasMinLength &&
    createUserPasswordValidation.hasUppercase &&
    createUserPasswordValidation.hasLowercase &&
    createUserPasswordValidation.hasNumber &&
    createUserPasswordValidation.passwordsMatch;

  // handlers
  const handleResetPasswordDialogOpenChange = (isOpen: boolean) => {
    setShowResetPasswordDialog(isOpen);

    if (isOpen) {
      return;
    }

    setSelectedUser(null);
    setNewPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    scheduleDialogLayerCleanup();
  };

  const handleChangeRoleDialogOpenChange = (isOpen: boolean) => {
    setShowChangeRoleDialog(isOpen);

    if (isOpen) {
      return;
    }

    setSelectedUser(null);
    scheduleDialogLayerCleanup();
  };

  const handleCreateUserDialogOpenChange = (isOpen: boolean) => {
    setShowCreateUserDialog(isOpen);

    if (isOpen) {
      return;
    }

    setCreateUserEmail("");
    setCreateUserName("");
    setCreateUserPassword("");
    setCreateUserConfirmPassword("");
    setCreateUserRole("staff");
    setShowCreatePassword(false);
    setShowCreateConfirmPassword(false);
    scheduleDialogLayerCleanup();
  };

  const handleDeactivateDialogOpenChange = (isOpen: boolean) => {
    setShowDeactivateDialog(isOpen);

    if (isOpen) {
      return;
    }

    setUserToDeactivate(null);
    scheduleDialogLayerCleanup();
  };

  const handleReactivateDialogOpenChange = (isOpen: boolean) => {
    setShowReactivateDialog(isOpen);

    if (isOpen) {
      return;
    }

    setUserToReactivate(null);
    scheduleDialogLayerCleanup();
  };

  const handleRoleChange = (value: string) => {
    if (!isUserRole(value)) {
      toast.error(
        "That role is not available. Choose admin, staff, or trainee."
      );
      return;
    }

    setNewRole(value);
  };

  const handleCreateUserRoleChange = (value: string) => {
    if (!isUserRole(value)) {
      toast.error(
        "That role is not available. Choose admin, staff, or trainee."
      );
      return;
    }

    setCreateUserRole(value);
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !isPasswordValid) return;

    setIsResetting(true);
    const result = await resetPassword({
      user_id: selectedUser._id,
      new_password: newPassword,
    });

    setIsResetting(false);

    if (result && result.error) {
      toast.error(result.error);
      return;
    }

    if (result && result.success) {
      if ("warning" in result && typeof result.warning === "string") {
        toast.warning(result.warning);
      } else {
        toast.success(`Password reset successful for ${selectedUser.email}`);
      }
      handleResetPasswordDialogOpenChange(false);
    }
  };

  const handleChangeRole = async () => {
    if (!selectedUser) return;

    setIsChangingRole(true);
    const result = await changeRole({
      user_id: selectedUser._id,
      role: newRole,
    });

    setIsChangingRole(false);

    if (result && result.error) {
      toast.error(result.error);
      return;
    }

    if (result && result.success) {
      toast.success(`Role changed to ${newRole} for ${selectedUser.email}`);
      handleChangeRoleDialogOpenChange(false);
    }
  };

  const openResetPasswordDialog = (user: ManagedUser) => {
    setSelectedUser(user);
    setNewPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setShowResetPasswordDialog(true);
  };

  const openChangeRoleDialog = (user: ManagedUser) => {
    setSelectedUser(user);
    setNewRole(user.role === null ? "staff" : user.role);
    setShowChangeRoleDialog(true);
  };

  const openCreateUserDialog = () => {
    setCreateUserEmail("");
    setCreateUserName("");
    setCreateUserPassword("");
    setCreateUserConfirmPassword("");
    setCreateUserRole("staff");
    setShowCreatePassword(false);
    setShowCreateConfirmPassword(false);
    setShowCreateUserDialog(true);
  };

  const handleCreateUser = async () => {
    if (!isCreateUserValid) return;

    setIsCreatingUser(true);
    const result = await createUser({
      email: createUserEmail,
      name: createUserName,
      password: createUserPassword,
      role: createUserRole,
    });

    setIsCreatingUser(false);

    if (result && result.error) {
      toast.error(result.error);
      return;
    }

    if (result && result.success) {
      toast.success(`User ${createUserEmail} created successfully`);
      handleCreateUserDialogOpenChange(false);
    }
  };

  const openDeactivateDialog = (user: ManagedUser) => {
    setUserToDeactivate(user);
    setShowDeactivateDialog(true);
  };

  const openReactivateDialog = (user: ManagedUser) => {
    setUserToReactivate(user);
    setShowReactivateDialog(true);
  };

  const handleDeactivateUser = async () => {
    if (!userToDeactivate) return;

    setIsDeactivating(true);
    const result = await deactivateUser({
      user_id: userToDeactivate._id,
    });

    setIsDeactivating(false);

    if (result && result.error) {
      toast.error(result.error);
      return;
    }

    if (result && result.success) {
      toast.success(`${userToDeactivate.email} deactivated.`);
      handleDeactivateDialogOpenChange(false);
    }
  };

  const handleReactivateUser = async () => {
    if (!userToReactivate) return;

    setIsReactivating(true);
    const result = await reactivateUser({
      user_id: userToReactivate._id,
    });

    setIsReactivating(false);

    if (result && result.error) {
      toast.error(result.error);
      return;
    }

    if (result && result.success) {
      toast.success(`${userToReactivate.email} reactivated.`);
      handleReactivateDialogOpenChange(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize));
    setCurrentPage(1);
  };

  // generate page numbers
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("ellipsis");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (!users) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">
            User Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <div className="text-sm text-muted-foreground">
              Loading users...
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">
              User Management
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={openCreateUserDialog}
              className="active:scale-95 transition-all duration-150 ease-out cursor-pointer"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create User
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {/* search and filters */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by email or name..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-9 text-sm"
                spellCheck={false}
                autoComplete="off"
              />
            </div>
            <Select
              value={String(pageSize)}
              onValueChange={handlePageSizeChange}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size} per page
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* results summary */}
          <div className="mb-3 text-xs text-muted-foreground">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)}{" "}
            of {filteredUsers.length} users
          </div>

          {/* table */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs font-medium">User</TableHead>
                  <TableHead className="text-xs font-medium">Role</TableHead>
                  <TableHead className="text-xs font-medium">
                    Created Date
                  </TableHead>
                  <TableHead className="text-xs font-medium text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-12">
                      <div className="text-sm text-muted-foreground">
                        No users found
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((user) => (
                    <TableRow
                      key={user._id}
                      className={
                        user.is_active === false ? "opacity-60 bg-muted/30" : ""
                      }
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                            <User className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-xs truncate">
                              {user.name}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {user.role ? (
                            <Badge
                              variant="secondary"
                              className={`text-xs ${ROLE_COLORS[user.role]}`}
                            >
                              {user.role}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              No role
                            </span>
                          )}
                          {user.is_active === false && (
                            <Badge
                              variant="secondary"
                              className="text-xs bg-red-100 text-red-800"
                            >
                              Deactivated
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {format(new Date(user.created_at), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu modal={false}>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 active:scale-95 transition-all duration-150 ease-out"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            onCloseAutoFocus={(event) =>
                              event.preventDefault()
                            }
                          >
                            <DropdownMenuItem
                              onSelect={() =>
                                openDialogAfterDropdownCloses(() =>
                                  openResetPasswordDialog(user)
                                )
                              }
                              className="cursor-pointer"
                            >
                              <KeyRound className="h-4 w-4 mr-2" />
                              Reset password
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={() =>
                                openDialogAfterDropdownCloses(() =>
                                  openChangeRoleDialog(user)
                                )
                              }
                              className="cursor-pointer"
                            >
                              <Shield className="h-4 w-4 mr-2" />
                              Change role
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {user.is_active === false ? (
                              <DropdownMenuItem
                                onSelect={() =>
                                  openDialogAfterDropdownCloses(() =>
                                    openReactivateDialog(user)
                                  )
                                }
                                className="cursor-pointer text-green-700 focus:text-green-700"
                              >
                                <UserCheck className="h-4 w-4 mr-2" />
                                Reactivate user
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onSelect={() =>
                                  openDialogAfterDropdownCloses(() =>
                                    openDeactivateDialog(user)
                                  )
                                }
                                disabled={
                                  currentUser !== undefined &&
                                  currentUser !== null &&
                                  currentUser._id === user._id
                                }
                                className="cursor-pointer text-red-600 focus:text-red-600"
                              >
                                <UserX className="h-4 w-4 mr-2" />
                                Deactivate user
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() =>
                        currentPage > 1 && handlePageChange(currentPage - 1)
                      }
                      className={
                        currentPage === 1
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer active:scale-95 transition-all duration-150 ease-out"
                      }
                    />
                  </PaginationItem>
                  {getPageNumbers().map((page, index) =>
                    page === "ellipsis" ? (
                      <PaginationItem key={`ellipsis-${index}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => handlePageChange(page as number)}
                          isActive={currentPage === page}
                          className="cursor-pointer active:scale-95 transition-all duration-150 ease-out"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  )}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() =>
                        currentPage < totalPages &&
                        handlePageChange(currentPage + 1)
                      }
                      className={
                        currentPage === totalPages
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer active:scale-95 transition-all duration-150 ease-out"
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>

      {/* reset password dialog */}
      <Dialog
        open={showResetPasswordDialog}
        onOpenChange={handleResetPasswordDialogOpenChange}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">
              Reset Password
            </DialogTitle>
            <DialogDescription className="text-sm">
              Reset password for {getUserEmailLabel(selectedUser)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-sm font-medium">
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="pr-10 text-sm"
                  spellCheck={false}
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-sm font-medium">
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="pr-10 text-sm"
                  spellCheck={false}
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* password requirements */}
            {newPassword.length > 0 && (
              <div className="space-y-2 p-3 bg-muted rounded-lg">
                <div className="text-xs font-medium text-muted-foreground">
                  Password Requirements:
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs">
                    {passwordValidation.hasMinLength ? (
                      <Check className="h-3 w-3 text-green-600" />
                    ) : (
                      <X className="h-3 w-3 text-red-600" />
                    )}
                    <span
                      className={
                        passwordValidation.hasMinLength
                          ? "text-green-600"
                          : "text-muted-foreground"
                      }
                    >
                      At least 6 characters
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {passwordValidation.hasUppercase ? (
                      <Check className="h-3 w-3 text-green-600" />
                    ) : (
                      <X className="h-3 w-3 text-red-600" />
                    )}
                    <span
                      className={
                        passwordValidation.hasUppercase
                          ? "text-green-600"
                          : "text-muted-foreground"
                      }
                    >
                      At least one uppercase letter
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {passwordValidation.hasLowercase ? (
                      <Check className="h-3 w-3 text-green-600" />
                    ) : (
                      <X className="h-3 w-3 text-red-600" />
                    )}
                    <span
                      className={
                        passwordValidation.hasLowercase
                          ? "text-green-600"
                          : "text-muted-foreground"
                      }
                    >
                      At least one lowercase letter
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {passwordValidation.hasNumber ? (
                      <Check className="h-3 w-3 text-green-600" />
                    ) : (
                      <X className="h-3 w-3 text-red-600" />
                    )}
                    <span
                      className={
                        passwordValidation.hasNumber
                          ? "text-green-600"
                          : "text-muted-foreground"
                      }
                    >
                      At least one number
                    </span>
                  </div>
                  {confirmPassword.length > 0 && (
                    <div className="flex items-center gap-2 text-xs">
                      {passwordValidation.passwordsMatch ? (
                        <Check className="h-3 w-3 text-green-600" />
                      ) : (
                        <X className="h-3 w-3 text-red-600" />
                      )}
                      <span
                        className={
                          passwordValidation.passwordsMatch
                            ? "text-green-600"
                            : "text-muted-foreground"
                        }
                      >
                        Passwords match
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleResetPasswordDialogOpenChange(false)}
              disabled={isResetting}
              className="active:scale-95 transition-all duration-150 ease-out"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={!isPasswordValid || isResetting}
              className="active:scale-95 transition-all duration-150 ease-out"
            >
              {isResetting ? "Resetting..." : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* change role dialog */}
      <Dialog
        open={showChangeRoleDialog}
        onOpenChange={handleChangeRoleDialogOpenChange}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">
              Change Role
            </DialogTitle>
            <DialogDescription className="text-sm">
              Change role for {getUserEmailLabel(selectedUser)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="role" className="text-sm font-medium">
                Role
              </Label>
              <Select value={newRole} onValueChange={handleRoleChange}>
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="trainee">Trainee</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
              <div className="text-xs text-yellow-800">
                <div className="font-medium mb-1">Role Permissions:</div>
                <div className="space-y-1">
                  <div>
                    <span className="font-medium">Admin:</span> Full access to
                    all features including user management
                  </div>
                  <div>
                    <span className="font-medium">Staff:</span> Can manage
                    properties, contacts, and areas
                  </div>
                  <div>
                    <span className="font-medium">Trainee:</span> Limited access
                    to view and create properties
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleChangeRoleDialogOpenChange(false)}
              disabled={isChangingRole}
              className="active:scale-95 transition-all duration-150 ease-out"
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangeRole}
              disabled={isChangingRole}
              className="active:scale-95 transition-all duration-150 ease-out"
            >
              {isChangingRole ? "Changing..." : "Change Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* create user dialog */}
      <Dialog
        open={showCreateUserDialog}
        onOpenChange={handleCreateUserDialogOpenChange}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">
              Create New User
            </DialogTitle>
            <DialogDescription className="text-sm">
              Create a new user account with email and password
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-email" className="text-sm font-medium">
                Email
              </Label>
              <Input
                id="create-email"
                type="email"
                value={createUserEmail}
                onChange={(e) => setCreateUserEmail(e.target.value)}
                placeholder="user@example.com"
                className="text-sm"
                spellCheck={false}
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-name" className="text-sm font-medium">
                Full Name
              </Label>
              <Input
                id="create-name"
                type="text"
                value={createUserName}
                onChange={(e) => setCreateUserName(e.target.value)}
                placeholder="John Doe"
                className="text-sm"
                spellCheck={false}
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-password" className="text-sm font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="create-password"
                  type={showCreatePassword ? "text" : "password"}
                  value={createUserPassword}
                  onChange={(e) => setCreateUserPassword(e.target.value)}
                  placeholder="Enter password"
                  className="pr-10 text-sm"
                  spellCheck={false}
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowCreatePassword(!showCreatePassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showCreatePassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="create-confirm-password"
                className="text-sm font-medium"
              >
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="create-confirm-password"
                  type={showCreateConfirmPassword ? "text" : "password"}
                  value={createUserConfirmPassword}
                  onChange={(e) => setCreateUserConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  className="pr-10 text-sm"
                  spellCheck={false}
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() =>
                    setShowCreateConfirmPassword(!showCreateConfirmPassword)
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showCreateConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-role" className="text-sm font-medium">
                Role
              </Label>
              <Select
                value={createUserRole}
                onValueChange={handleCreateUserRoleChange}
              >
                <SelectTrigger id="create-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="trainee">Trainee</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* password requirements */}
            {createUserPassword.length > 0 && (
              <div className="space-y-2 p-3 bg-muted rounded-lg">
                <div className="text-xs font-medium text-muted-foreground">
                  Password Requirements:
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs">
                    {createUserPasswordValidation.hasMinLength ? (
                      <Check className="h-3 w-3 text-green-600" />
                    ) : (
                      <X className="h-3 w-3 text-red-600" />
                    )}
                    <span
                      className={
                        createUserPasswordValidation.hasMinLength
                          ? "text-green-600"
                          : "text-muted-foreground"
                      }
                    >
                      At least 6 characters
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {createUserPasswordValidation.hasUppercase ? (
                      <Check className="h-3 w-3 text-green-600" />
                    ) : (
                      <X className="h-3 w-3 text-red-600" />
                    )}
                    <span
                      className={
                        createUserPasswordValidation.hasUppercase
                          ? "text-green-600"
                          : "text-muted-foreground"
                      }
                    >
                      At least one uppercase letter
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {createUserPasswordValidation.hasLowercase ? (
                      <Check className="h-3 w-3 text-green-600" />
                    ) : (
                      <X className="h-3 w-3 text-red-600" />
                    )}
                    <span
                      className={
                        createUserPasswordValidation.hasLowercase
                          ? "text-green-600"
                          : "text-muted-foreground"
                      }
                    >
                      At least one lowercase letter
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {createUserPasswordValidation.hasNumber ? (
                      <Check className="h-3 w-3 text-green-600" />
                    ) : (
                      <X className="h-3 w-3 text-red-600" />
                    )}
                    <span
                      className={
                        createUserPasswordValidation.hasNumber
                          ? "text-green-600"
                          : "text-muted-foreground"
                      }
                    >
                      At least one number
                    </span>
                  </div>
                  {createUserConfirmPassword.length > 0 && (
                    <div className="flex items-center gap-2 text-xs">
                      {createUserPasswordValidation.passwordsMatch ? (
                        <Check className="h-3 w-3 text-green-600" />
                      ) : (
                        <X className="h-3 w-3 text-red-600" />
                      )}
                      <span
                        className={
                          createUserPasswordValidation.passwordsMatch
                            ? "text-green-600"
                            : "text-muted-foreground"
                        }
                      >
                        Passwords match
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleCreateUserDialogOpenChange(false)}
              disabled={isCreatingUser}
              className="active:scale-95 transition-all duration-150 ease-out"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={!isCreateUserValid || isCreatingUser}
              className="active:scale-95 transition-all duration-150 ease-out"
            >
              {isCreatingUser ? "Creating..." : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* deactivate user confirmation dialog */}
      <AlertDialog
        open={showDeactivateDialog}
        onOpenChange={handleDeactivateDialogOpenChange}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-semibold">
              Deactivate user
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              Deactivate {getUserEmailLabel(userToDeactivate)}? They will no
              longer be able to sign in, but their data will be preserved. An
              admin can reactivate them later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isDeactivating}
              className="active:scale-95 transition-all duration-150 ease-out"
            >
              Keep user active
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleDeactivateUser();
              }}
              disabled={isDeactivating}
              className="bg-red-600 hover:bg-red-700 active:scale-95 transition-all duration-150 ease-out"
            >
              {isDeactivating ? "Deactivating user" : "Deactivate user"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* reactivate user confirmation dialog */}
      <AlertDialog
        open={showReactivateDialog}
        onOpenChange={handleReactivateDialogOpenChange}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-semibold">
              Reactivate user
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              Reactivate {getUserEmailLabel(userToReactivate)}? They will be
              able to sign in again with their existing role.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isReactivating}
              className="active:scale-95 transition-all duration-150 ease-out"
            >
              Keep user deactivated
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleReactivateUser();
              }}
              disabled={isReactivating}
              className="bg-green-700 hover:bg-green-800 active:scale-95 transition-all duration-150 ease-out"
            >
              {isReactivating ? "Reactivating user" : "Reactivate user"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
